import { Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { FiatIcon, VisibilityIcon, WalletDefaultIcon } from 'BreetAssets';
import { Amount, LoadingComponent, Tooltip, TooltipContent } from 'BreetComponents';
import {
	capitalizeFirstLetters,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	type CURRENCYSYMBOLTYPE,
	getSafeObjectField,
} from 'BreetHelpers';
import { useWalletBalance, useWithdrawRequestApi } from 'BreetHooks';
import { selectIsFiatBalanceHidden, selectUserData, setIsFiatBalanceHidden, useAppDispatch, useAppSelector } from 'BreetRedux';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { IWalletBalance } from './WalletBalance.helpers';

const WalletBalance = ({ withdraw, fiat, isSettlementWallet }: IWalletBalance) => {
	const { withdrawFunds, withdrawFundsUsd } = useWithdrawRequestApi();

	const user = useAppSelector(selectUserData);

	const dispatch = useAppDispatch();

	const hideAmount = useAppSelector(selectIsFiatBalanceHidden);
	const hiddenAmount = '*******';

	const { getBalance, wallets, isWalletBalanceLoading } = useWalletBalance();

	const balance = getBalance(fiat);

	const walletBalance = wallets && !hideAmount ? balance : hiddenAmount;

	const handleVisibility = useCallback(() => {
		if (withdraw) return;

		dispatch(setIsFiatBalanceHidden(!hideAmount));
	}, [dispatch, hideAmount, withdraw]);

	const uiConfigObj: Record<
		CURRENCYCODETYPE,
		{ currency: CURRENCYCODETYPE; symbol: CURRENCYSYMBOLTYPE; withdrawHandler?: () => void }
	> = {
		USD: {
			currency: CURRENCYCONFIG.USD.code,
			symbol: CURRENCYCONFIG.USD.symbol,
			withdrawHandler: withdrawFundsUsd,
		},
		NGN: {
			currency: CURRENCYCONFIG.NGN.code,
			symbol: CURRENCYCONFIG.NGN.symbol,
			withdrawHandler: withdrawFunds,
		},
		GHS: {
			currency: CURRENCYCONFIG.GHS.code,
			symbol: CURRENCYCONFIG.GHS.symbol,
			withdrawHandler: withdrawFunds,
		},
	};

	const uiConfig = uiConfigObj[fiat];

	const currencyShort = getSafeObjectField(CURRENCYCONFIG, uiConfig.currency)?.currencyShort ?? '';

	const navigate = useNavigate();

	const goToSettings = useCallback(() => {
		navigate('/settings/crypto-settings')?.catch(console.error);
	}, [navigate]);

	const isRolePermitted =
		user?.businessRole === 'owner' || user?.businessRole === 'super_admin' || user?.businessRole === 'subadmin';

	return (
		<div
			className={classNameMerger('card walletbalance', 'bg-primary-400 border-dark-navy')}
			data-testid='wallet-balance'
		>
			<div className='walletbalance_content'>
				<div className='walletbalance_content_header'>
					<LoadingComponent loading={isWalletBalanceLoading}>
						<div className='currency-tag bg-primary-500 border-dark-navy'>
							<FiatIcon
								currencyCode={uiConfig.currency}
								variant='round'
							/>
							<Typography className='color-neutral-50'>
								{capitalizeFirstLetters(currencyShort)}
								{uiConfig.currency === 'GHS' ? 's' : ''} ({uiConfig.currency})
							</Typography>
						</div>
					</LoadingComponent>

					<LoadingComponent loading={isWalletBalanceLoading}>
						{isSettlementWallet && (
							<Tooltip
								tipElement={
									<TooltipContent
										content={`Every Crypto-to-${currencyShort} transactions will be settled here. ${isRolePermitted ? 'To change it, go to' : ''}`}
										linkTitle={isRolePermitted ? 'Settings' : undefined}
										onLinkClick={isRolePermitted ? goToSettings : undefined}
									/>
								}
							>
								<WalletDefaultIcon />
							</Tooltip>
						)}
					</LoadingComponent>
				</div>

				<LoadingComponent loading={isWalletBalanceLoading}>
					<div className='walletbalance_content_body'>
						<Typography
							as='h6'
							className='color-neutral-50'
						>
							<span>{hideAmount ? '' : uiConfig.symbol}</span>
							{!hideAmount ? <Amount>{walletBalance}</Amount> : walletBalance}
						</Typography>

						<VisibilityIcon
							show={hideAmount}
							className={classNameMerger(
								'svg-dark svg-dark-outline',
								'walletbalance_content_visibility cursor_pointer',
								hideAmount && 'hidden-amount'
							)}
							onClick={handleVisibility}
							role='button'
							aria-label={hideAmount ? 'Show balance' : 'Hide balance'}
							aria-pressed={hideAmount}
							tabIndex={0}
						/>
					</div>
				</LoadingComponent>
			</div>
		</div>
	);
};

export default WalletBalance;
